import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import {
  useCreateMarketZoneBreakdown,
  CreateMarketZoneBreakdownSchema,
} from "@/lib/api/risk/marketZoneBreakthrough";
import { MarketZoneBreakdownForm } from "./MarketZoneBreakdownForm";
import { useListRiskSummaryFull } from "@/lib/api/risk/summary";

interface CreateMarketZoneBreakdownDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialData?: {
    customer_id?: string;
    market_zone_id?: string;
    commodity?: string;
    crop_year?: number;
  };
  baseTopLevelResult?: ReturnType<typeof useListRiskSummaryFull>
}

interface MarketZoneBreakdownFormData {
  customer_id: string;
  market_zone_id: string;
  commodity: string;
  crop_year: number;
  month_one?: string;
  month_two?: string;
  month_three?: string;
  month_four?: string;
  month_five?: string;
  month_six?: string;
  month_seven?: string;
  month_eight?: string;
  month_nine?: string;
  month_ten?: string;
  month_eleven?: string;
  month_twelve?: string;
  total: string;
  amount: string;
  note?: string;
}

export function CreateMarketZoneBreakdownDialog({
  isOpen,
  onClose,
  onSuccess,
  initialData,
  baseTopLevelResult,
}: CreateMarketZoneBreakdownDialogProps) {
  const { toast } = useToast();
  const createMarketZoneBreakdown = useCreateMarketZoneBreakdown();
  const [isCreating, setIsCreating] = useState(false);

  const handleSubmit = async (data: MarketZoneBreakdownFormData) => {
    setIsCreating(true);
    try {
      // Prepare data for API - convert empty strings to undefined for optional fields
      const preData = {
        customer_id: data.customer_id,
        market_zone_id: data.market_zone_id,
        commodity: data.commodity,
        crop_year: data.crop_year,
        month_one: data.month_one?.toString() || undefined,
        month_two: data.month_two?.toString() || undefined,
        month_three: data.month_three?.toString() || undefined,
        month_four: data.month_four?.toString() || undefined,
        month_five: data.month_five?.toString() || undefined,
        month_six: data.month_six?.toString() || undefined,
        month_seven: data.month_seven?.toString() || undefined,
        month_eight: data.month_eight?.toString() || undefined,
        month_nine: data.month_nine?.toString() || undefined,
        month_ten: data.month_ten?.toString() || undefined,
        month_eleven: data.month_eleven?.toString() || undefined,
        month_twelve: data.month_twelve?.toString() || undefined,
        total: data.total?.toString() || "0",
        amount: data.amount?.toString(),
        note: data.note || undefined,
      };

      // Validate the data
      const validationResult = CreateMarketZoneBreakdownSchema.safeParse(preData);

      if (!validationResult.success) {
        const errors = validationResult.error.errors
          .map((err) => `${err.path.join(".")}: ${err.message}`)
          .join("\n");
        console.error("Failed to validate create breakdown", preData, errors);

        toast({
          title: "Invalid Data",
          description: "Please check your input and try again.",
          variant: "destructive",
        });
        return;
      }

      const createData = validationResult.data;
      const result = await createMarketZoneBreakdown(createData);

      if (!result.success) {
        throw result;
      }

      toast({
        title: "Success",
        description: "Created",
      });
      onSuccess();
    } catch (error) {
      console.error("Failed to create market zone breakdown", error);
      toast({
        title: "Error",
        description:
          error instanceof Error ? error.message : "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[90vw] md:max-w-[80vw] lg:max-w-[70vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Market Zone Breakdown</DialogTitle>
          <DialogDescription>
          </DialogDescription>
        </DialogHeader>

        <MarketZoneBreakdownForm
          baseTopLevelResult={baseTopLevelResult}
          mode="create"
          onSubmit={handleSubmit}
          onCancel={handleClose}
          showCancelButton={true}
          isSubmitting={isCreating}
          initialData={{
            customer_id: initialData?.customer_id || "",
            market_zone_id: initialData?.market_zone_id || "",
            commodity: initialData?.commodity || "",
            crop_year: initialData?.crop_year || new Date().getFullYear(),
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
